// src/steps/Step4CurrentKnowledge.js
import { AssistantNote } from "../components/common/AssistantFields";
import React from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
  FrameworkSelector,
  EDU_MLUS,
  GOV_MLUS,
  TEC_MLUS,
  EID_MLUS,
} from "../components/FrameworkSelector";

import FrameworkHeader from "../components/FrameworkHeader";

/** Shown only when an AI assistant sent a prior-knowledge summary through the connector (MCP). */
function AssistantSummaryNote({ summary, matchedIds }) {
  const [open, setOpen] = React.useState(false);
  if (!summary) return null;
  const n = matchedIds.length;
  return (
    <Alert severity="info" icon={<span aria-hidden>🤖</span>} sx={{ mb: 2 }} role="status">
      Your AI assistant sent a summary of what the learner already knows.{" "}
      {n > 0
        ? <>The tool marked {n} MLU{n === 1 ? "" : "s"} below (🤖). Please review them, change them if needed, and confirm.</>
        : <>The tool did not mark any MLUs from it. Please tick the ones the learner already knows.</>}
      <Box>
        <Button size="small" onClick={() => setOpen((o) => !o)} sx={{ textTransform: "none", px: 0, mt: 0.5 }}>
          {open ? "Hide the summary" : "Show the summary"}
        </Button>
        <Collapse in={open}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.5, color: "text.secondary" }}>
            {summary}
          </Typography>
        </Collapse>
      </Box>
    </Alert>
  );
}

export default function Step4CurrentKnowledge({
  assistantSummary = "",
  matchedIds = [],
  matchWhy = {},
  selEdu,
  setSelEdu,
  selGov,
  setSelGov,
  selTech,
  setSelTech,
  selEth,
  setSelEth,
  certificationNeeded,
  // onSave,    // handled globally via StepNavigationBar
  // sessionId, // no longer needed locally
}) {
  // Helper to create framework config in a DRY way
  const frameworks = [
    {
      id: "EDU",
      title: "Education (EDU)",
      tooltip:
        "Education (EDU) MLUs focus on foundational concepts, learning pathways, and capacity building for working with Local Digital Twins.",
      mlus: EDU_MLUS,
      selected: selEdu,
      setSelected: setSelEdu,
    },
    {
      id: "EID",
      title: "Ethics, Inclusion & Democratisation (EID)",
      tooltip:
        "EID MLUs address ethical use of data & AI, inclusive participation, and democratisation of Local Digital Twin practices.",
      mlus: EID_MLUS,
      selected: selEth,
      setSelected: setSelEth,
    },
    {
      id: "GOV",
      title: "Governance (GOV)",
      tooltip:
        "Governance (GOV) MLUs address decision-making structures, coordination across departments, and the policy context of Local Digital Twins.",
      mlus: GOV_MLUS,
      selected: selGov,
      setSelected: setSelGov,
    },
    {
      id: "TEC",
      title: "Technical Design (TEC)",
      tooltip:
        "Technical Design (TEC) MLUs focus on data architectures, modelling, integration, and infrastructure for Local Digital Twins.",
      mlus: TEC_MLUS,
      selected: selTech,
      setSelected: setSelTech,
    },
  ];

  return (
    <Box>
      {/* ---- Step title + info icon ---- */}
      <Box display="flex" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ mr: 1 }}>
          Step 4 · Current Knowledge (per framework)
        </Typography>

        <Tooltip
          title={
            <Box>
              <Typography sx={{ fontSize: 16, lineHeight: 1.5, mb: 1 }}>
                Which topics are already known and familiar to the learner(s)?
                If the learner(s) have zero knowledge, proceed without selecting
                anything.
              </Typography>

              <Box sx={{ mt: 1 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.4,
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Abbreviations of the MLUs:
                </Typography>
                <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>
                  EDU – Education
                </Typography>
                <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>
                  EID – Ethics, Inclusion & Democratisation
                </Typography>
                <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>
                  GOV – Governance
                </Typography>
                <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>
                  TEC – Technical Design
                </Typography>
              </Box>

              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>
                  The first digit of an MLU (e.g. EDU-101) refers to the module
                  (1, 2, or 3). The last digit (e.g. EDU-101) refers to
                  difficulty (1–2 beginner, 3–4 intermediate, 5–6 advanced).
                </Typography>
              </Box>
            </Box>
          }
          componentsProps={{
            tooltip: {
              sx: { maxWidth: 420, padding: 2 },
            },
          }}
        >
          <IconButton size="small" sx={{ color: "black", p: 0.5 }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Click on the MLUs the learner/the group of learners already know(s).
      </Typography>

      <AssistantSummaryNote summary={assistantSummary} matchedIds={matchedIds} />

      {/* ---- Framework sections (EDU, EID, GOV, TEC) ---- */}
      {frameworks.map((fw, idx) => (
        <Box key={fw.id} mt={3}>
          <FrameworkHeader title={fw.title} tooltip={fw.tooltip} />
          <AssistantNote field={{ EDU: "education", GOV: "governance", TEC: "technical", EID: "ethics" }[fw.id]} />

          <FrameworkSelector
            label=""
            codePrefix={fw.id}
            mlus={fw.mlus}
            selected={fw.selected}
            certificationNeeded={certificationNeeded}
            aiIds={matchedIds}
            aiWhy={matchWhy}
            onToggle={(code) =>
              fw.setSelected((prev) =>
                prev.includes(code)
                  ? prev.filter((c) => c !== code)
                  : [...prev, code]
              )
            }
          />
        </Box>
      ))}

      {/* Local "Save Step 4" button removed – handled by global navigation */}
    </Box>
  );
}
